import {
  type ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import CommandPalette from "./CommandPalette";

/**
 * Public shape of the command palette context.
 * Consumers should not call `setOpen` directly — use `openPalette()` instead.
 */
export interface CommandPaletteContextValue {
  /** Whether the palette is currently rendered open. */
  isOpen: boolean;
  /** Open the palette. Optionally seed the search input. */
  openPalette: (initialQuery?: string) => void;
  /** Close the palette imperatively. */
  closePalette: () => void;
  /** Toggle the palette open/closed (used by ⌘K). */
  togglePalette: () => void;
}

export const CommandPaletteContext = createContext<
  CommandPaletteContextValue | undefined
>(undefined);

interface CommandPaletteProviderProps {
  children: ReactNode;
}

/**
 * Mounts the global ⌘K / Ctrl+K command palette once and exposes
 * imperative open/close handles via React context.
 *
 * Placed inside `GlobalLayout` so it only activates for authenticated routes.
 */
const CommandPaletteProvider = ({ children }: CommandPaletteProviderProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [initialQuery, setInitialQuery] = useState<string | undefined>(
    undefined,
  );

  const openPalette = useCallback((query?: string) => {
    setInitialQuery(query);
    setIsOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setIsOpen(false);
  }, []);

  const togglePalette = useCallback(() => {
    setIsOpen((current) => !current);
    setInitialQuery(undefined);
  }, []);

  // Global ⌘K / Ctrl+K listener. Skips when user is typing in inputs
  // that need the shortcut for themselves (rare) — we still allow it
  // because palette opens over everything via portal.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const isMetaK =
        (event.metaKey || event.ctrlKey) && event.key?.toLowerCase() === "k";
      if (!isMetaK) return;
      event.preventDefault();
      togglePalette();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [togglePalette]);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ isOpen, openPalette, closePalette, togglePalette }),
    [isOpen, openPalette, closePalette, togglePalette],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPalette
        open={isOpen}
        onOpenChange={(next) => {
          setIsOpen(next);
          if (!next) setInitialQuery(undefined);
        }}
        initialQuery={initialQuery}
      />
    </CommandPaletteContext.Provider>
  );
};

export default CommandPaletteProvider;
